import React, { useState, useEffect } from 'react';
import { getCalendarSessions } from '../utils/api';
import './Calendar.css';

function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetails, setDayDetails] = useState([]);

  useEffect(() => {
    loadTrainingSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const loadTrainingSessions = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await getCalendarSessions(year, month);
      setTrainingSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to load training sessions:', err);
      setTrainingSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const getSessionsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return trainingSessions.filter(session => session.training_date.startsWith(dateStr));
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    setSelectedDate(null);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const sessions = getSessionsForDay(day);
    if (sessions.length > 0) {
      setSelectedDate(day);
      setDayDetails(sessions);
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const monthNamesTH = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

  const days = getDaysInMonth();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekDaysTH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

  return (
    <div className="calendar-page">
      <div className="calendar-header">
        <span className="eyebrow-pill">Calendar</span>
        <h1 className="calendar-title">
          Training <em>Calendar</em>
        </h1>
      </div>

      <div className="calendar-container">
        <div className="calendar-controls">
          <button onClick={handlePrevMonth} className="month-nav-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <h2 className="current-month">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            <span className="month-th"> / {monthNamesTH[currentDate.getMonth()]} {currentDate.getFullYear() + 543}</span>
          </h2>
          <button onClick={handleNextMonth} className="month-nav-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="calendar-loading">
            <div className="spinner"></div>
            <p>Loading calendar...</p>
          </div>
        ) : (
          <div className="calendar-grid">
            <div className="calendar-weekdays">
              {weekDays.map((day, index) => (
                <div key={day} className="weekday">
                  {day} <span className="weekday-th">/ {weekDaysTH[index]}</span>
                </div>
              ))}
            </div>
            <div className="calendar-days">
              {days.map((day, index) => {
                const sessions = getSessionsForDay(day);
                const hasTraining = sessions.length > 0;
                const isToday = day &&
                  day === new Date().getDate() &&
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear();

                return (
                  <div
                    key={index}
                    className={`calendar-day ${!day ? 'empty' : ''} ${hasTraining ? 'has-training' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => handleDayClick(day)}
                  >
                    {day && (
                      <>
                        <div className="day-number">{day}</div>
                        {hasTraining && (
                          <div className="training-indicators">
                            {sessions.slice(0, 3).map((session, idx) => (
                              <div key={idx} className="training-dot" title={session.training_topic}>
                                <span className="dot-icon">•</span>
                              </div>
                            ))}
                            {sessions.length > 3 && (
                              <div className="more-sessions">+{sessions.length - 3}</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Day Details Modal */}
      {selectedDate && dayDetails.length > 0 && (
        <div className="modal-overlay" onClick={() => setSelectedDate(null)}>
          <div className="modal-content day-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {monthNames[currentDate.getMonth()]} {selectedDate}, {currentDate.getFullYear()}
              </h2>
              <button className="close-btn" onClick={() => setSelectedDate(null)}>×</button>
            </div>

            <div className="modal-body">
              <div className="sessions-count">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <span>{dayDetails.length} Training {dayDetails.length === 1 ? 'Session' : 'Sessions'}</span>
              </div>

              <div className="day-sessions-list">
                {dayDetails.map((session, index) => (
                  <div key={index} className="day-session-card">
                    <div className="session-topic-main">{session.training_topic}</div>
                    <div className="session-meta-row">
                      <div className="session-meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                          <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                        </svg>
                        <span>Trainer: {session.trainer_name || 'N/A'}</span>
                      </div>
                      <div className="session-meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span>{session.training_hours}h</span>
                      </div>
                    </div>
                    <div className="session-department">{session.department}</div>
                    {session.trainee_names && (
                      <div className="session-trainees">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <span>Trainees: {session.trainee_names}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;
